import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  Home,
  Key,
  Link as LinkIcon,
  ScrollText,
  Settings,
  Bell,
} from "lucide-react";
import { HotelDetails } from "./hotel/HotelDetails";
import { PMSCredentials } from "./hotel/PMSCredentials";
import { Integrations } from "./hotel/Integrations";
import { PMSLogs } from "./hotel/PMSLogs";
import { HotelSettings } from "./hotel/HotelSettings";
import { UserMenu } from "../UserMenu";
import { ThemeToggle } from "../ThemeToggle";
import { ThemeProvider } from "../../contexts/ThemeContext";
import { NotificationBell } from "../shared/NotificationBell";
import { NotificationCenter } from "../shared/NotificationCenter";
import { useExtranetSection } from "../../hooks/useExtranetSection";
import { HotelSection } from "../../lib/routes";

export function HotelExtranet() {
  const location = useLocation();
  // The section lives in the URL (/extranet/hotel/:section), so every page is
  // bookmarkable and the browser's back button works between them.
  const { section: activeSection, sectionPath, goToSection } =
    useExtranetSection("hotel");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserEmail(user.email || "");
      }
    };
    getUser();

    // Check if we should show settings with user tab. The state is carried
    // over so Settings still opens on the "user" tab; the section guard stops
    // the redirect from firing again once we're there.
    if (location.state?.settingsTab === "user" && activeSection !== "settings") {
      goToSection("settings", { replace: true, state: location.state });
    }
  }, [location.state, activeSection, goToSection]);

  const renderContent = () => {
    switch (activeSection) {
      case "details":
        return <HotelDetails />;
      case "credentials":
        return <PMSCredentials />;
      case "integrations":
        return <Integrations />;
      case "logs":
        return <PMSLogs />;
      case "notifications":
        return <NotificationCenter />;
      case "settings":
        return <HotelSettings />;
      default:
        return <HotelDetails />;
    }
  };

  const menuItems: { id: HotelSection; label: string; icon: typeof Home }[] = [
    { id: "details", label: "Hotel Details", icon: Home },
    { id: "credentials", label: "Connector Credentials", icon: Key },
    { id: "integrations", label: "Integrations", icon: LinkIcon },
    { id: "logs", label: "PMS Logs", icon: ScrollText },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <ThemeProvider>
      <div className="extranet-layout">
        <div className="extranet-sidebar">
          {/* Logo */}
          <div className="p-4">
            <div className="flex flex-col">
              <div className="w-32">
                <img
                  src="/images/omnylink_logo.png"
                  alt="Omnylink"
                  className="w-full h-auto block"
                />
                <img
                  src="/images/omnylink_logo_white.png"
                  alt="Omnylink"
                  className="w-full h-auto hidden"
                />
              </div>
              <span className="mt-1 text-sm font-medium text-gray-600">
                for Hotels
              </span>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={sectionPath(item.id)}
                  aria-current={activeSection === item.id ? "page" : undefined}
                  className={`
                    w-full flex items-center px-3 py-2 text-sm font-medium rounded-md
                    ${
                      activeSection === item.id
                        ? "bg-indigo-100"
                        : "text-gray-600"
                    }
                  `}
                >
                  <Icon
                    className={`
                    h-5 w-5 mr-3
                    ${
                      activeSection === item.id
                        ? "text-blue-500"
                        : "text-gray-400"
                    }
                  `}
                  />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="p-4 flex items-center justify-between">
            <UserMenu email={userEmail} onOpenSettings={() => goToSection("settings")} />
          </div>
        </div>

        {/* Main Content */}
        <div className="extranet-content">
          {/* Header with notification bell */}
          <div className="bg-white border-b border-gray-200 px-4 py-2 flex justify-end items-center">
            <NotificationBell />
          </div>
          <div className="extranet-content-inner">{renderContent()}</div>
        </div>
      </div>
    </ThemeProvider>
  );
}
